import { typeahead } from "@mirage/service-typeahead-search/service/types";

/**
 * Array with a maximum length of 10 items
 * NOTE: Runtime validation is required to enforce this constraint
 */
export type MaxLengthArray<T, N extends number> = T[] & { __maxLength?: N };

export type SectionIdUnion = typeahead.SectionId | "__none__";

export type RecentSectionTrack = {
  timestamp: number;
  sectionScore: number;
  resultUuid: string;
};

export type SectionTrackingRecord = {
  [K in SectionIdUnion]: {
    count: number;
    recentTracks: MaxLengthArray<RecentSectionTrack, 10>;
  };
};

export function constrainRecentTracks(
  tracks: RecentSectionTrack[],
): MaxLengthArray<RecentSectionTrack, 10> {
  const sortedTracks = [...tracks]
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, 10);

  return sortedTracks as MaxLengthArray<RecentSectionTrack, 10>;
}

export function addTrackToSection(
  record: SectionTrackingRecord,
  sectionId: SectionIdUnion,
  track: RecentSectionTrack,
): SectionTrackingRecord {
  let section = record[sectionId];

  if (!section) {
    section = {
      count: 0,
      recentTracks: [],
    };
  }

  const updatedTracks = constrainRecentTracks([
    track,
    ...(section.recentTracks || []),
  ]);

  return {
    ...record,
    [sectionId]: {
      count: section.count + 1,
      recentTracks: updatedTracks,
    },
  };
}

export function createEmptySectionTrackingRecord(): SectionTrackingRecord {
  return Object.values(typeahead.SectionId).reduce((acc, sectionId) => {
    return {
      ...acc,
      [sectionId]: {
        count: 0,
        recentTracks: [] as MaxLengthArray<RecentSectionTrack, 10>,
      },
    };
  }, {} as SectionTrackingRecord);
}
